/**
 * The confirm step between "Upgrade" and Stripe.
 *
 * A panel, not a modal — only destructive confirmation and sign-in earn a
 * modal. Four lines, one button, and it says the real amount before the domain
 * changes, so `checkout.stripe.com` reads as expected rather than alarming.
 */
import { TIER_LIMITS, TIER_TAGLINES } from "../../shared/tiers";
import { api } from "../api";
import {
  annualSaving,
  checkoutSummary,
  explainBillingFailure,
  formatPrice,
  type Interval,
  type PaidTier,
  PRICES,
} from "../api/billing";
import { identity, onIdentityChange } from "../auth/session";
import { openSignInSheet } from "../auth/sign-in-sheet";
import { actionButton, el, replace } from "./dom";
import { tierChip } from "./tier-chip";

/**
 * What the plan actually buys, with the editor's own icons.
 *
 * Icons here are not decoration duplicating a heading — rule 3 — they are the
 * same glyphs from the tool rail, so "seam tools" is recognisable as a control
 * the reader will meet rather than a phrase in a sales list. A list rather than
 * cards, because three parallel things read faster as a list and the visual
 * language says so.
 *
 * Counts come from TIER_LIMITS so the page cannot promise a number the server
 * will not honour.
 */
const BENEFITS: Record<PaidTier, { icon: string; text: string }[]> = {
  creator: [
    {
      icon: "i-saved-cloud",
      text: `${TIER_LIMITS.creator.maxProjects} designs in the cloud, ${TIER_LIMITS.creator.versionRetention} versions of each`,
    },
    { icon: "i-edge-check", text: "Edge Doctor and seam bleed — no stray pixels at distance" },
    // Was "Baked shading and motion preview". Shading was built, measured and
    // retired — the bake darkened the faces nobody sees and left the torso
    // front untouched — so the line was advertising something that no longer
    // exists. This one is the actual paid difference and it already ships.
    { icon: "i-palette", text: "Every fabric, in any colour you like" },
  ],
  studio: [
    { icon: "i-colourways", text: "Colourways and rank sets from one design" },
    { icon: "i-share", text: "Client proofing links" },
    { icon: "i-palette", text: "Brand kits and batch export" },
  ],
};

/** A sprite reference. The sheet is already on the page. */
function icon(name: string): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "icon icon-sm");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("href", `#${name}`);
  svg.appendChild(use);
  return svg;
}

function benefitList(tier: PaidTier): HTMLElement {
  const list = el("ul", { class: "hud-upgrade__benefits" });
  for (const { icon: name, text } of BENEFITS[tier]) {
    const item = el("li");
    item.appendChild(icon(name));
    item.appendChild(el("span", { text }));
    list.appendChild(item);
  }
  return list;
}

export interface UpgradePanelOptions {
  tier?: PaidTier;
  /** Annual by default — it is the better deal and the honest one to show. */
  interval?: Interval;
  onClose?: () => void;
}

export interface UpgradePanel {
  el: HTMLElement;
  destroy(): void;
}

/**
 * The choice, carried in the URL.
 *
 * Signing in is a redirect, so anything held in a variable is gone by the time
 * they come back. Putting it in the query string means someone who picks
 * annual Studio, signs in, and returns lands on annual Studio rather than on
 * the default with their decision quietly discarded.
 */
function readSelection(): { tier?: PaidTier; interval?: Interval } {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const tier = params.get("plan");
  const interval = params.get("billing");
  return {
    tier: tier === "creator" || tier === "studio" ? tier : undefined,
    interval: interval === "monthly" || interval === "annual" ? interval : undefined,
  };
}

function rememberSelection(tier: PaidTier, interval: Interval) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("plan", tier);
  url.searchParams.set("billing", interval);
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
}

export function createUpgradePanel(options: UpgradePanelOptions = {}): UpgradePanel {
  const root = el("section", { class: "hud-upgrade", aria: { label: "Plans" } });
  const saved = readSelection();
  let tier: PaidTier = options.tier ?? saved.tier ?? "creator";
  let interval: Interval = options.interval ?? saved.interval ?? "annual";
  const error = el("p", { class: "hud-upgrade__error", hidden: true, aria: { live: "polite" } });

  function render() {
    const sellable = identity().sellableTiers;
    if (sellable.length > 0 && !sellable.includes(tier)) tier = sellable[0] as PaidTier;

    if (sellable.length === 0) {
      // Prices are not wired in this environment. Say so plainly rather than
      // showing a button that 400s.
      replace(
        root,
        el("p", {
          class: "hud-upgrade__unavailable",
          text: "Plans are not available on this site yet.",
        }),
      );
      return;
    }

    const summary = checkoutSummary(tier, interval);
    const saving = annualSaving(tier);

    replace(
      root,
      // The plan as its chip rather than as a word. Same information, but it
      // carries the plan's colour, which every later mention of that plan can
      // then lean on instead of re-explaining itself.
      el("h2", { class: "hud-upgrade__heading" }, tierChip(tier, { solid: true })),
      el("p", { class: "hud-upgrade__tagline", text: TIER_TAGLINES[tier] }),

      benefitList(tier),

      // The toggle. Both options identically styled — it is a real choice, not
      // a decoy, and the monthly price is never hidden to make annual look
      // inevitable.
      el(
        "div",
        { class: "hud-upgrade__intervals", aria: { label: "Billing interval" } },
        intervalOption("monthly", `${formatPrice(PRICES[tier].monthly)} / month`),
        intervalOption(
          "annual",
          `${formatPrice(PRICES[tier].annual)} / year`,
          // The arithmetic, not a percentage in a pill. A number that can be
          // checked reads as trustworthy.
          `${saving.perMonth}/month — save ${saving.saved}`,
        ),
      ),

      error,

      // The button, and the amount only when the amount has something left to
      // say. checkoutSummary returns null for charge whenever the first charge
      // matches the recurring one, which is every case today — the price is on
      // the card directly above, and stating it twice more does not make it
      // truer. When a trial or intro price exists the line comes back, which
      // is the moment it is actually a disclosure.
      el(
        "div",
        { class: "hud-upgrade__actions" },
        actionButton(
          identity().signedIn || identity().devUser ? summary.action : "Sign in to subscribe",
          go,
          { class: "hud-upgrade__go btn-glow", data: { icon: "arrow" } },
        ),
        summary.charge ? el("p", { class: "hud-upgrade__charge", text: summary.charge }) : null,
        options.onClose
          ? actionButton("Not now", () => options.onClose?.(), { class: "hud-upgrade__cancel" })
          : null,
      ),

      el("p", { class: "hud-upgrade__note", text: summary.reassurance }),
    );
  }

  function intervalOption(value: Interval, label: string, detail?: string) {
    return el(
      "button",
      {
        class: "hud-upgrade__interval",
        type: "button",
        data: { selected: String(interval === value), interval: value },
        aria: { pressed: interval === value },
        on: {
          click: () => {
            interval = value;
            render();
          },
        },
      },
      el("span", { class: "hud-upgrade__interval-label", text: label }),
      detail ? el("span", { class: "hud-upgrade__interval-detail", text: detail }) : null,
    );
  }

  async function go() {
    error.hidden = true;

    // Signing in before paying, rather than after. Asking someone to
    // authenticate once they have already decided to hand over money is the
    // highest-abandonment moment in any funnel — and a checkout button that
    // 401s is worse still, which is what this did before.
    if (!identity().signedIn && !identity().devUser) {
      rememberSelection(tier, interval);
      await openSignInSheet({
        reason: "Sign in to subscribe",
        kind: "none",
        returnTo: window.location.pathname + window.location.search,
      });
      return;
    }

    try {
      const url = await api.billing.startCheckout(tier, interval);
      // A full navigation, not a popup: popups get blocked, and the back button
      // should return here rather than to a dead tab.
      window.location.assign(url);
    } catch (err) {
      error.textContent = explainBillingFailure(err);
      error.hidden = false;
    }
  }

  render();
  // The label depends on whether they are signed in, and identity resolves
  // after first paint. Without this the button keeps saying "Sign in to
  // subscribe" to somebody who already is.
  const stop = onIdentityChange(() => render());

  return {
    el: root,
    destroy: () => {
      stop();
      root.remove();
    },
  };
}

/** The moment the free quota runs out. Earned, not a wall. */
export function quotaPrompt(used: number, limit: number): string {
  if (used < limit) {
    const left = limit - used;
    return `${left} cloud project${left === 1 ? "" : "s"} left on Free.`;
  }
  return `That's your ${ordinal(limit)} saved design. Upgrade to keep more in the cloud — everything you have made stays where it is.`;
}

function ordinal(n: number): string {
  // 11th, 12th and 13th are the exceptions that break the last-digit rule.
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
